import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './lib/supabase';
import './Login.css';
import LanguageContext from './LanguageContext';
import { loginPageCopy } from './translations';

function Login() {
  const { language } = useContext(LanguageContext);
  const copy = loginPageCopy[language] || loginPageCopy.English;
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  
  // Show success message from signup if present
  useEffect(() => {
    if (location.state?.message) {
      // You can show a success message here if needed
      console.log(location.state.message);
    }
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = copy.errors.emailRequired;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = copy.errors.emailInvalid;
    }

    if (!formData.password) {
      newErrors.password = copy.errors.passwordRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    if (validateForm()) {
      setIsLoading(true);
      
      try {
        // Check user credentials in Supabase
        // Also check that user is not deleted
        const { data, error } = await supabase
          .from('users')
          .select('id, full_name, email, is_deleted')
          .eq('email', formData.email.toLowerCase().trim())
          .eq('password', formData.password) // Note: In production, compare hashed passwords
          .eq('is_deleted', false) // Only allow non-deleted users to login
          .single();
        
        if (error || !data) {
          setLoginError(copy.errors.invalidCredentials);
          setIsLoading(false);
          
          // Log failed login attempt (optional)
          try {
            await supabase.from('login_logs').insert([
              {
                email: formData.email.toLowerCase().trim(),
                login_success: false
              }
            ]);
          } catch (logError) {
            console.error('Failed to log login attempt:', logError);
          }
          return;
        }
        
        // Successful login
        console.log('Login successful:', data);
        
        // Store user session
        localStorage.setItem('userLoggedIn', 'true');
        localStorage.setItem('userId', data.id);
        localStorage.setItem('userEmail', data.email);
        localStorage.setItem('userName', data.full_name);
        
        // Trigger custom event so header updates immediately
        window.dispatchEvent(new Event('userLogin'));
        
        // Log successful login attempt (optional)
        try {
          await supabase.from('login_logs').insert([
            {
              user_id: data.id,
              email: data.email,
              login_success: true
            }
          ]);
        } catch (logError) {
          console.error('Failed to log login attempt:', logError);
        }
        
        // Navigate to home or dashboard
        navigate('/');
      } catch (err) {
        console.error('Unexpected error:', err);
        setLoginError(copy.errors.unexpected);
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Login Form */}
        <div className="auth-form-wrapper">
          <h2 className="auth-title">{copy.title}</h2>
          <p className="auth-subtitle">{copy.subtitle}</p>

          {loginError && (
            <div className="login-error-message" style={{
              backgroundColor: 'rgba(236, 72, 153, 0.1)',
              border: '1px solid rgba(236, 72, 153, 0.3)',
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '1.5rem',
              color: '#EC4899',
              fontSize: '0.9rem',
              textAlign: 'center'
            }}>
              {loginError}
            </div>
          )}

          {location.state?.message && (
            <div className="success-message" style={{
              backgroundColor: 'rgba(32, 178, 170, 0.1)',
              border: '1px solid rgba(32, 178, 170, 0.3)',
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '1.5rem',
              color: '#20B2AA',
              fontSize: '0.9rem',
              textAlign: 'center'
            }}>
              {location.state.message}
            </div>
          )}

          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <input
                type="email"
                name="email"
                id="email"
                placeholder={copy.emailPlaceholder}
                value={formData.email}
                onChange={handleChange}
                className={`form-input ${errors.email ? 'error' : ''}`}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <input
                type="password"
                name="password"
                id="password"
                placeholder={copy.passwordPlaceholder}
                value={formData.password}
                onChange={handleChange}
                className={`form-input ${errors.password ? 'error' : ''}`}
              />
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>

            <div className="form-options">
              <Link to="/forgot-password" className="forgot-password-link">
                {copy.forgotPassword}
              </Link>
            </div>

            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? copy.loggingIn : copy.loginButton}
            </button>
          </form>

          {/* Alternate Option */}
          <div className="auth-alternate">
            <p>
              {copy.alternateText}{' '}
              <Link to="/signup" className="auth-link">{copy.signUp}</Link>
            </p>
          </div>
        </div>

        {/* Privacy Note */}
        <div className="auth-footer">
          <p className="privacy-note">
            {copy.privacyNote}
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;



